import { onLogin } from '../me/login-event';
import { YPaymentAmount } from '../y/amount';
import { $api } from '../y/api';
import { makeDataStore } from '../y/data-store';
import { YI18NString, YLanguage } from '../y/i18n';
import { YFlags, YId, YTaggedId, YTimeString } from '../y/types';
import { CCityId } from './c-city';
import { CEventFlag, CSessionFlag } from './c-event';
import { CEventTagId } from './c-event-tag';
import { CProducerRole } from './c-producer-admin';
import { CVenueId, CVenueSceneId } from './c-venue';
import { EPayoutDestination } from './e-payout';
import { UMediaLink } from './u-media-link';

export type CProducerId = YTaggedId<'c_producer'>;
export type CProducerFlag = 'merge_booking_lists' | 'uses_booking_list';

export type CProducerEmailKind = 'billing' | 'booking_list' | 'fully_booked';

export interface CProducerEmail {
    email: string;
    kinds: CProducerEmailKind[];
}

export type CTicketType = 'pickup' | 'email';

export interface CEventDefaults {
    city_id?: CCityId;
    venue_id?: CVenueId;
    scene_id?: CVenueSceneId;
    description?: YI18NString;
    media_links?: UMediaLink[];
    picture_ids?: YId[];
    vat?: number;
    producer_emails?: CProducerEmail[];
    event_flags?: YFlags<CEventFlag>
    event_tag_ids?: CEventTagId[];
    is_drop_in?: boolean;
    sales_end?: number;
    duration?: number;
    ticket_type?: CTicketType;
    instructions?: YI18NString;
    base_price?: YPaymentAmount;
    payout_price?: YPaymentAmount;
    session_flags?: YFlags<CSessionFlag>
}

export interface CProducer {
    id: CProducerId;
    is_test: boolean;
    name: string;
    slug: string;
    language: YLanguage;
    email?: string;
    phone?: string;
    org_name?: string;
    org_number?: string;
    tax_id?: string;
    address?: string;
    payout_destination?: EPayoutDestination;
    event_defaults?: CEventDefaults;
    flags: YFlags<CProducerFlag>;
    created_at: YTimeString;
    updated_at: YTimeString;
}

interface CProducerAutofillBookingUser {
    name: string;
    email: string;
}

export interface CProducerAutofill {
    booking_users: CProducerAutofillBookingUser[];
}

export const c_producers = makeDataStore('producers', 'producer', (m: CProducer) => m, Object.assign);

export function cProducerHasValidPaymentAccount(producer: CProducer) {
    const dest = producer.payout_destination;
    return !!dest && !!dest.kind && !!dest.number;
}

export function cProducerSendMissingInfo(producer_id: YId) {
    return $api.POST(`producer/${producer_id}/send_missing_info`);
}

export function cProducerGetAutofill(producer_id: YId) {
    return $api.GET<CProducerAutofill>(`c/producer/${producer_id}/autofill`);
}

export function cProducerRoleInviteSend(email: string, producer_id: YId, roles: CProducerRole[]) {
    return $api.POST('producer/' + producer_id + '/invite', { email: email, roles: roles });
}

export function cProducerRoleInviteResend(id: YId) {
    return $api.POST('role_invite/' + id + '/resend');
}

export function cProducerRoleInviteDelete(invite_id: YId) {
    return $api.DELETE('role_invite/' + invite_id);
}

onLogin(data => {
    if(data.producers)
        c_producers.init(data.producers);
});
